import React, { useEffect, useState, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion } from 'framer-motion'
import { Check, Shield, ArrowRight, Loader2, Lock } from 'lucide-react'
import { apiService } from '../../services/api'
import { toast } from 'react-hot-toast'
import { useBranding } from '../../context/BrandingContext'
import { useAuth } from '../../context/AuthContext'

const STANDARD_ORDER = ['basic', 'pro', 'enterprise']

// ── Activating screen (post-Stripe redirect) ─────────────────────────────────
const ActivatingScreen = () => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-0, var(--bg-1))' }}>
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', textAlign: 'center', maxWidth: '24rem', padding: '0 1.5rem' }}
    >
      <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 style={{ width: 32, height: 32, color: 'var(--accent)', animation: 'spin 1s linear infinite' }} />
      </div>
      <div>
        <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--text)', marginBottom: 8 }}>
          Activating your subscription
        </h2>
        <p style={{ color: 'var(--text-muted)', lineHeight: 1.6 }}>
          Payment received — setting up your account. This only takes a moment.
        </p>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, color: 'var(--text-faint)', fontSize: 12.5 }}>
        <Lock style={{ width: 14, height: 14 }} />
        <span>Secured by Stripe</span>
      </div>
    </motion.div>
  </div>
)

// ── Plan card (matches prototype NSPlanGate cards) ───────────────────────────
const PlanCard = ({ plan, isSelected, isPopular, onSelect }) => {
  const featureList = [
    plan.features?.sites != null
      ? `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} website${plan.features.sites === 1 ? '' : 's'}`
      : null,
    plan.features?.editsPerMonth != null
      ? `${plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth} edit requests / mo`
      : null,
    plan.features?.storage ? `${plan.features.storage} storage` : null,
    plan.features?.support || null,
    plan.features?.customDomain ? 'Custom domain' : null,
    plan.features?.analytics ? 'Analytics' : null,
    plan.features?.backup ? 'Automated backups' : null,
  ].filter(Boolean)

  const desc = plan.description
    || (plan.features?.sites != null
        ? `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} website${plan.features.sites === 1 ? '' : 's'}`
        : '')

  return (
    <button
      type="button"
      onClick={() => onSelect(plan.id)}
      className={`card card-pad ${isSelected ? 'card-glow' : ''}`}
      style={{
        textAlign: 'left',
        cursor: 'pointer',
        border: `1.5px solid ${isSelected ? 'var(--border-accent)' : 'var(--border)'}`,
        background: isSelected ? 'var(--accent-soft)' : 'var(--bg-1)',
        transition: 'all 0.15s ease',
        position: 'relative',
      }}
    >
      {isPopular && (
        <span className="badge badge-accent" style={{ position: 'absolute', top: 14, right: 14 }}>
          Most popular
        </span>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: isPopular ? 18 : 0 }}>
        <h3 className="h3">{plan.name}</h3>
        <span style={{
          marginLeft: 'auto', width: 20, height: 20, borderRadius: '50%',
          border: `1.5px solid ${isSelected ? 'transparent' : 'var(--border-strong)'}`,
          background: isSelected ? 'var(--accent)' : 'transparent', color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          {isSelected && <Check style={{ width: 12, height: 12 }} />}
        </span>
      </div>

      <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)', marginTop: 10 }}>
        £{plan.price}
        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font)' }}> /month</span>
      </div>
      {desc && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, marginBottom: 16 }}>{desc}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: desc ? 0 : 16 }}>
        {featureList.map((feature) => (
          <div key={feature} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
            <Check style={{ width: 14, height: 14, color: isSelected ? 'var(--accent-3, var(--accent))' : 'var(--success)', flexShrink: 0 }} />
            <span style={{ color: 'var(--text-soft)' }}>{feature}</span>
          </div>
        ))}
      </div>
    </button>
  )
}

// ── Main gate ─────────────────────────────────────────────────────────────────
const SubscriptionGate = ({ children }) => {
  const branding = useBranding()
  const { cmsName } = branding
  const logoSrc = branding.logoUrl || branding.logoUrlLight
  const { user } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedPlan, setSelectedPlan] = useState(null)

  const isSuccessPage = location.pathname.endsWith('/billing/success')
  const sessionId = new URLSearchParams(location.search).get('session_id')
  const isPostPayment = isSuccessPage && !!sessionId

  const { data: profile, isLoading: profileLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(r => r.data),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
      refetchInterval: isPostPayment ? 3000 : false,
    }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { enabled: !profileLoading }
  )

  // Custom plans are email-scoped: only those targeted to this client are offered.
  const { data: availableCustom } = useQuery(
    ['available-custom-plans', profile?.email],
    () => apiService.get(`/custom-plans/available/${encodeURIComponent(profile.email)}`).then(r => r.data),
    { enabled: !!profile?.email }
  )

  const billingStatus = profile?.billing_status
  const assignedPlanPrice = plansRaw?.[profile?.subscription_plan]?.price
  const isFreePlan = assignedPlanPrice === 0
  const isActive = billingStatus === 'active' || billingStatus === 'trialing' || isFreePlan

  const email = (profile?.email || '').toLowerCase()
  const assignedPlanId = profile?.subscription_plan

  // Build the list of plans to offer:
  //  • If a custom plan is assigned to this client — either their subscription_plan
  //    points at a custom plan, or a custom plan explicitly targets their email —
  //    show ONLY those custom plans and hide the standard defaults.
  //  • Otherwise, show the standard default plans.
  const plans = useMemo(() => {
    if (!plansRaw) return []

    const assignedIds = new Set(
      (availableCustom || [])
        .filter(p => Array.isArray(p.targetEmails) && p.targetEmails.some(e => (e || '').toLowerCase() === email))
        .map(p => p.id)
    )
    if (assignedPlanId && plansRaw[assignedPlanId]?.custom) assignedIds.add(assignedPlanId)

    if (assignedIds.size > 0) {
      return [...assignedIds].filter(id => plansRaw[id]).map(id => ({ id, ...plansRaw[id] }))
    }
    return STANDARD_ORDER.filter(id => plansRaw[id]).map(id => ({ id, ...plansRaw[id] }))
  }, [plansRaw, availableCustom, email, assignedPlanId])

  // Keep the selection valid for whatever plans are visible.
  useEffect(() => {
    if (!plans.length) return
    setSelectedPlan(prev =>
      prev && plans.some(p => p.id === prev)
        ? prev
        : (plans.find(p => p.id === profile?.subscription_plan)?.id || plans[0].id)
    )
  }, [plans, profile])

  const checkoutMutation = useMutation(
    (planId) => apiService.post('/billing/create-checkout-session', { planId }).then(r => r.data),
    {
      onSuccess: (data) => {
        if (data.free) {
          // Free plan activated server-side — unlock the portal immediately.
          queryClient.invalidateQueries('client-profile')
          navigate('/client', { replace: true })
        } else if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl
        } else {
          toast.error('No checkout URL returned. Please try again.')
        }
      },
      onError: () => toast.error('Failed to start checkout. Please try again.')
    }
  )

  useEffect(() => {
    if (isPostPayment && isActive) {
      navigate('/client', { replace: true })
    }
  }, [isActive, isPostPayment, navigate])

  // On return from Stripe, verify the checkout session server-side to activate
  // the subscription immediately — don't rely solely on webhook delivery, which
  // otherwise leaves the client stuck on the "Activating…" screen.
  useEffect(() => {
    if (!isPostPayment || !sessionId) return
    let cancelled = false
    let attempts = 0
    const verify = async () => {
      attempts += 1
      try {
        const { data } = await apiService.post('/billing/verify-session', { sessionId })
        if (cancelled) return
        if (data?.active) {
          await queryClient.invalidateQueries('client-profile')
          navigate('/client', { replace: true })
          return
        }
      } catch (_) {
        // transient — retry below
      }
      if (!cancelled && attempts < 10) setTimeout(verify, 2000)
    }
    verify()
    return () => { cancelled = true }
  }, [isPostPayment, sessionId, queryClient, navigate])

  if (profileLoading) return <>{children}</>
  if (isActive) return <>{children}</>
  if (isPostPayment) return <ActivatingScreen />

  const selected = plans.find(p => p.id === selectedPlan)
  const isFreeSelected = selected && Number(selected.price) <= 0
  const firstName = (user?.name || profile?.user_name || '').trim().split(' ')[0]

  return (
    <div className="drawer-backdrop" style={{ justifyContent: 'center', alignItems: 'center', animation: 'fadeIn 0.16s ease', zIndex: 140, overflowY: 'auto' }}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="card"
        style={{ width: 940, maxWidth: '95vw', maxHeight: '94vh', overflowY: 'auto', padding: '34px 36px', boxShadow: 'var(--shadow-lg)' }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', maxWidth: 520, margin: '0 auto 26px' }}>
          <div style={{ width: 52, height: 52, borderRadius: 15, margin: '0 auto 16px', background: 'linear-gradient(135deg, #A78BFA, #5B7CFF)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 10px 28px -10px var(--accent-glow, rgba(108,99,255,0.5))' }}>
            {logoSrc
              ? <img src={logoSrc} alt={cmsName} style={{ width: 34, height: 34, objectFit: 'contain' }} />
              : (
                <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
                  <path d="M8 8.5c1.6-2 6.4-2 8 0M8 12c1.6-2 6.4-2 8 0M8 15.5c1.6-2 6.4-2 8 0" stroke="white" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              )}
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--text)' }}>
            Choose a plan to continue
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, lineHeight: 1.6, marginTop: 10 }}>
            Welcome to {cmsName}{firstName ? `, ${firstName}` : ''} 👋 Your workspace is ready — pick a plan to unlock your portal. You can change or cancel anytime.
          </p>
        </div>

        {/* Plan cards */}
        {plans.length > 0 ? (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(plans.length, 3)}, 1fr)`, gap: 14 }}>
            {plans.map(plan => (
              <PlanCard
                key={plan.id}
                plan={plan}
                isSelected={selectedPlan === plan.id}
                isPopular={plan.id === (profile?.subscription_plan || 'pro')}
                onSelect={setSelectedPlan}
              />
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 160 }}>
            <Loader2 style={{ width: 24, height: 24, color: 'var(--text-faint)', animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 26, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <Shield style={{ width: 14, height: 14, color: 'var(--success)' }} />
            {isFreeSelected
              ? 'No payment required · cancel anytime'
              : 'Payments secured by Stripe · cancel anytime'}
          </span>
          <button
            className="btn btn-primary"
            style={{ marginLeft: 'auto', padding: '11px 26px', fontSize: 14 }}
            onClick={() => checkoutMutation.mutate(selectedPlan)}
            disabled={!selectedPlan || checkoutMutation.isLoading || plans.length === 0}
          >
            {checkoutMutation.isLoading ? (
              <>
                <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />
                {isFreeSelected ? 'Activating…' : 'Redirecting to Stripe…'}
              </>
            ) : (
              <>
                {`Continue with ${selected?.name || 'plan'}`}
                <ArrowRight style={{ width: 16, height: 16 }} />
              </>
            )}
          </button>
        </div>

        {/* Already paid notice */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 18, fontSize: 12.5, color: 'var(--text-muted)' }}>
          <span>Already paid? <button onClick={() => window.location.reload()} style={{ color: 'var(--accent-3, var(--accent))', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Refresh this page</button></span>
        </div>
      </motion.div>
    </div>
  )
}

export default SubscriptionGate
